<template>
  <div class="animated fadeIn salary-formula-setup-page">
    <div class="card">
      <div class="main-card-body">
        <div class="salary-formula-setup__header">
          <div>
            <h4 class="salary-formula-setup__title">Thiết lập công thức tính lương</h4>
            <div class="salary-formula-setup__selector">
              <div class="salary-formula-setup__selector-row">
                <span class="salary-formula-setup__selector-label">Cột cần thiết lập</span>
                <el-select
                  :value="currentFormulaCode"
                  filterable
                  class="salary-formula-setup__selector-input"
                  placeholder="Chọn cột cần thiết lập công thức"
                  @change="handleFormulaColumnChange"
                >
                  <el-option
                    v-for="item in editableFormulaColumns"
                    :key="item.code"
                    :label="`${item.displayName}`"
                    :value="item.code"
                  ></el-option>
                </el-select>

                <span class="salary-formula-setup__selector-label">Loại hợp đồng</span>
                <el-select
                  :value="selectedContractTypeId"
                  filterable
                  class="salary-formula-setup__selector-input salary-formula-setup__selector-input--contract"
                  placeholder="Chọn loại hợp đồng"
                  @change="handleContractTypeChange"
                >
                  <el-option
                    v-for="item in contractTypes"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  ></el-option>
                </el-select>
              </div>
            </div>
          </div>
          <div class="salary-formula-setup__actions">
            <el-button
              type="primary"
              class="salary-formula-setup__button"
              @click="goBack"
            >
              Quay lại
            </el-button>
          </div>
        </div>

        <div class="salary-formula-setup__hint">
          Ở dòng Công thức tính, thiết lập công thức bằng cách nhấn đúp chuột vào dòng ký hiệu
          các cột để chọn vào công thức, sử dụng các phép (+, -, *, /) hoặc các ký hiệu
          khác trên giao diện để thiết lập.
        </div>

        <div class="salary-formula-setup__layout">
          <div class="salary-formula-setup__left">
            <div class="salary-formula-setup__section-title">Công thức tính</div>
            <div class="salary-formula-setup__editor-wrap">
              <el-input
                ref="formulaEditor"
                v-model="formulaEditorValue"
                type="textarea"
                :rows="8"
                class="salary-formula-setup__editor"
              ></el-input>
            </div>

            <div class="salary-formula-setup__operator-bar">
              <div class="salary-formula-setup__operator-list">
                <el-button
                  v-for="operator in operators"
                  :key="operator"
                  size="mini"
                  plain
                  @click="insertOperator(operator)"
                >
                  {{ operator }}
                </el-button>
              </div>
              <div class="salary-formula-setup__formula-actions">
                <el-button size="mini" type="primary" @click="resetFormulaInput">Nhập lại</el-button>
                <el-button size="mini" type="primary" @click="saveFormula">Lưu</el-button>
              </div>
            </div>

            <div class="salary-formula-setup__section-title">Diễn giải</div>
            <div class="salary-formula-setup__description-wrap">
              <div class="salary-formula-setup__description" v-text="generatedDescription"></div>
            </div>
          </div>

          <div class="salary-formula-setup__right">
            <div class="salary-formula-setup__section-title">Chỉ tiêu</div>
            <div class="salary-formula-setup__table-wrap">
              <el-table
                :data="formulaColumns"
                border
                height="100%"
                class="salary-formula-setup__table"
                :header-cell-style="getHeaderCellStyle"
                @row-dblclick="insertFieldToken"
              >
                <el-table-column
                  label=""
                  width="68"
                  align="center"
                  class-name="salary-formula-setup__action-column"
                >
                  <template slot-scope="scope">
                    <button
                      type="button"
                      class="salary-formula-setup__insert-button"
                      @click.stop="insertFieldToken(scope.row)"
                    >
                      <i class="el-icon-arrow-left"></i>
                    </button>
                  </template>
                </el-table-column>
                <el-table-column prop="displayName" label="Tên cột" min-width="180">
                </el-table-column>
                <el-table-column label="Mã cột" min-width="180">
                  <template slot-scope="scope">
                    <span>{{ getVisibleCode(scope.row) }}</span>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ALL_CONTRACT_TYPE_FORMULA_KEY,
  buildSalaryFormulaApiPayload,
  getDefaultSalaryFormulaColumns,
  mapSalaryFormulaApiItemsToConfigs,
} from "../shared/salaryFormulaConfig";
import ContractTypeApi from "../../../../moduleApi/ContractTypeApi";
import SalaryFormulaConfigApi from "../../../../moduleApi/SalaryFormulaConfigApi";

const FORMULA_FUNCTION_LABELS = {
  FLOOR: "lay_phan_nguyen",
  MAX: "gia_tri_lon_nhat",
  MIN: "gia_tri_nho_nhat",
  TAXRATE: "thue_suat",
};

const FORMULA_FUNCTION_REVERSE_LABELS = Object.keys(FORMULA_FUNCTION_LABELS).reduce(
  (result, key) => {
    result[FORMULA_FUNCTION_LABELS[key]] = key;
    return result;
  },
  {}
);

export default {
  data() {
    return {
      formulaColumns: [],
      formulaDraftMap: {},
      currentFormulaCode: "",
      selectedContractTypeId: "",
      formulaEditorValue: "",
      operators: ["+", "-", "*", "/", "(", ")", ","],
      contractTypes: [],
    };
  },

  computed: {
    editableFormulaColumns() {
      return this.formulaColumns.filter((item) => this.canEditFormula(item));
    },

    currentFormulaRow() {
      return this.formulaColumns.find((item) => item.code === this.currentFormulaCode) || {};
    },

    currentDisplayName() {
      return this.currentFormulaRow.displayName || "";
    },

    generatedDescription() {
      return this.buildFormulaDescription(this.formulaEditorValue, this.currentDisplayName);
    },
  },

  async mounted() {
    await Promise.all([
      this.loadContractTypes(),
      this.loadFormulaConfigs(),
    ]);
    this.handleFormulaColumnChange(this.currentFormulaCode, false);
  },

  methods: {
    async loadContractTypes() {
      try {
        const response = await ContractTypeApi.list({ limit: 1000000, filters: "disabled!=true" }, true);
        this.contractTypes = Array.isArray(response && response.result) ? response.result : [];
        this.selectedContractTypeId = this.getSelectedContractTypeFromRoute();
      } catch (error) {
        this.contractTypes = [];
        this.selectedContractTypeId = ALL_CONTRACT_TYPE_FORMULA_KEY;
      }
    },

    getSelectedContractTypeFromRoute() {
      const requestedContractTypeId = `${(this.$route.query && this.$route.query.contractTypeId) || ""}`.trim();
      if (requestedContractTypeId === ALL_CONTRACT_TYPE_FORMULA_KEY) {
        return requestedContractTypeId;
      }

      const matchedContractType = this.contractTypes.find((item) => item && item.id === requestedContractTypeId);
      if (matchedContractType) {
        return requestedContractTypeId;
      }

      return this.contractTypes.length && this.contractTypes[0].id
        ? this.contractTypes[0].id
        : ALL_CONTRACT_TYPE_FORMULA_KEY;
    },

    getFormulaDraftKey(code = this.currentFormulaCode, contractTypeId = this.selectedContractTypeId) {
      return `${code || ""}__${contractTypeId || ALL_CONTRACT_TYPE_FORMULA_KEY}`;
    },

    syncCurrentFormulaDraft() {
      if (!this.currentFormulaCode) {
        return;
      }
      this.$set(
        this.formulaDraftMap,
        this.getFormulaDraftKey(this.currentFormulaCode, this.selectedContractTypeId),
        this.formulaEditorValue
      );
    },

    getEditorFormulaBySelection(code = this.currentFormulaCode, contractTypeId = this.selectedContractTypeId) {
      const draftKey = this.getFormulaDraftKey(code, contractTypeId);
      if (this.formulaDraftMap[draftKey] !== undefined) {
        return this.formulaDraftMap[draftKey];
      }

      if (contractTypeId !== ALL_CONTRACT_TYPE_FORMULA_KEY) {
        const defaultDraftKey = this.getFormulaDraftKey(code, ALL_CONTRACT_TYPE_FORMULA_KEY);
        if (this.formulaDraftMap[defaultDraftKey] !== undefined) {
          return this.formulaDraftMap[defaultDraftKey];
        }
      }

      return "";
    },

    async loadFormulaConfigs() {
      try {
        const response = await SalaryFormulaConfigApi.list({ limit: 1000000 }, true);
        const apiConfigs = Array.isArray(response && response.result) ? response.result : [];
        this.formulaColumns = mapSalaryFormulaApiItemsToConfigs(apiConfigs);
      } catch (error) {
        this.formulaColumns = getDefaultSalaryFormulaColumns();
      }
      this.formulaDraftMap = this.formulaColumns.reduce((result, item) => {
        const contractTypeFormulas =
          item.contractTypeFormulas && typeof item.contractTypeFormulas === "object"
            ? item.contractTypeFormulas
            : { [ALL_CONTRACT_TYPE_FORMULA_KEY]: item.formula || "" };

        Object.keys(contractTypeFormulas).forEach((contractTypeId) => {
          result[this.getFormulaDraftKey(item.code, contractTypeId)] = this.convertRawFormulaToEditor(
            contractTypeFormulas[contractTypeId] || ""
          );
        });

        return result;
      }, {});

      const requestedCode = `${this.$route.query.code || ""}`.trim();
      const defaultRow =
        this.editableFormulaColumns.find((item) => item.code === requestedCode) ||
        this.editableFormulaColumns[0] ||
        this.formulaColumns[0] ||
        {};
      this.handleFormulaColumnChange(defaultRow.code || "", false);
    },

    canEditFormula(row) {
      if (!row) {
        return false;
      }

      if (`${row.formula || ""}`.trim()) {
        return true;
      }

      const contractTypeFormulas =
        row.contractTypeFormulas && typeof row.contractTypeFormulas === "object"
          ? row.contractTypeFormulas
          : {};

      return Object.keys(contractTypeFormulas).some((key) => `${contractTypeFormulas[key] || ""}`.trim());
    },

    handleFormulaColumnChange(code, shouldSyncCurrentDraft = true) {
      if (shouldSyncCurrentDraft) {
        this.syncCurrentFormulaDraft();
      }
      this.currentFormulaCode = code || "";
      this.formulaEditorValue = this.getEditorFormulaBySelection(
        this.currentFormulaCode,
        this.selectedContractTypeId
      );
    },

    handleContractTypeChange(contractTypeId) {
      this.syncCurrentFormulaDraft();
      this.selectedContractTypeId = contractTypeId || ALL_CONTRACT_TYPE_FORMULA_KEY;
      this.formulaEditorValue = this.getEditorFormulaBySelection(
        this.currentFormulaCode,
        this.selectedContractTypeId
      );
      this.$router
        .replace({
          name: this.$route.name,
          query: {
            ...this.$route.query,
            contractTypeId: this.selectedContractTypeId,
          },
        })
        .catch(() => {});
    },

    getVisibleCode(row) {
      if (!row) {
        return "";
      }
      return row.codeLabel || row.code || "";
    },

    getFormulaLabelByCode(code, columns = this.formulaColumns) {
      const foundItem = (columns || []).find((item) => item.code === code);
      return foundItem && foundItem.displayName ? foundItem.displayName : code;
    },

    getFormulaLabelByToken(token, columns = this.formulaColumns) {
      const normalizedToken = `${token || ""}`.trim();
      const foundItem = (columns || []).find((item) => {
        return item.code === normalizedToken || item.codeLabel === normalizedToken;
      });
      return foundItem && foundItem.displayName ? foundItem.displayName : `[${normalizedToken}]`;
    },

    getFormulaCodeDisplay(code, columns = this.formulaColumns) {
      const foundItem = (columns || []).find((item) => item.code === code);
      return foundItem && foundItem.codeLabel ? foundItem.codeLabel : code;
    },

    convertRawFormulaToEditor(formula) {
      let editorFormula = `${formula || ""}`.trim();
      const formulaCodeItems = (this.formulaColumns || [])
        .map((item) => item && item.code)
        .filter(Boolean)
        .sort((first, second) => second.length - first.length);

      editorFormula = editorFormula.replace(/\[([^\]]+)\]/g, (_, token) => {
        return `[${this.getFormulaCodeDisplay(token.trim())}]`;
      });

      formulaCodeItems.forEach((code) => {
        const escapedCode = code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        editorFormula = editorFormula.replace(
          new RegExp(`(^|[^A-Za-z0-9_\\]])(${escapedCode})(?=[^A-Za-z0-9_]|$)`, "g"),
          (match, prefix, token) => `${prefix}[${this.getFormulaCodeDisplay(token)}]`
        );
      });

      Object.keys(FORMULA_FUNCTION_LABELS).forEach((functionName) => {
        editorFormula = editorFormula.replace(
          new RegExp(`\\b${functionName}\\b`, "g"),
          FORMULA_FUNCTION_LABELS[functionName]
        );
      });

      return editorFormula;
    },

    convertEditorFormulaToRaw(formula) {
      let rawFormula = `${formula || ""}`.trim();
      const codeLabelItems = (this.formulaColumns || [])
        .map((item) => item && item.codeLabel)
        .filter(Boolean)
        .sort((first, second) => second.length - first.length);

      rawFormula = rawFormula.replace(/\[([^\]]+)\]/g, (_, token) => {
        const normalizedToken = token.trim();
        const foundItem = (this.formulaColumns || []).find((item) => {
          return item.codeLabel === normalizedToken || item.code === normalizedToken;
        });
        return foundItem ? `[${foundItem.code}]` : `[${normalizedToken}]`;
      });

      codeLabelItems.forEach((codeLabel) => {
        const foundItem = (this.formulaColumns || []).find((item) => item.codeLabel === codeLabel);
        if (!foundItem) {
          return;
        }
        const escapedCodeLabel = codeLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        rawFormula = rawFormula.replace(
          new RegExp(`(^|[^A-Za-z0-9_\\]])(${escapedCodeLabel})(?=[^A-Za-z0-9_]|$)`, "g"),
          (match, prefix) => `${prefix}[${foundItem.code}]`
        );
      });

      Object.keys(FORMULA_FUNCTION_REVERSE_LABELS).forEach((functionLabel) => {
        rawFormula = rawFormula.replace(
          new RegExp(`\\b${functionLabel}\\b`, "g"),
          FORMULA_FUNCTION_REVERSE_LABELS[functionLabel]
        );
      });

      return rawFormula;
    },

    buildFormulaDescription(formula, displayName, columns = this.formulaColumns) {
      const normalizedFormula = `${formula || ""}`.trim();
      const normalizedDisplayName = `${displayName || ""}`.trim();
      if (!normalizedFormula) {
        return "";
      }

      let humanReadableFormula = normalizedFormula.replace(/\[([^\]]+)\]/g, (_, token) => {
        return this.getFormulaLabelByToken(token, columns);
      });

      const codeLabelItems = (columns || [])
        .map((item) => item && item.codeLabel)
        .filter(Boolean)
        .sort((first, second) => second.length - first.length);

      codeLabelItems.forEach((codeLabel) => {
        const foundItem = (columns || []).find((item) => item.codeLabel === codeLabel);
        if (!foundItem) {
          return;
        }
        const escapedCodeLabel = codeLabel.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        humanReadableFormula = humanReadableFormula.replace(
          new RegExp(`(^|[^A-Za-z0-9_\\]])(${escapedCodeLabel})(?=[^A-Za-z0-9_]|$)`, "g"),
          (match, prefix) => `${prefix}${foundItem.displayName}`
        );
      });

      const specialDescription = this.buildSpecialFormulaDescription(
        normalizedFormula,
        normalizedDisplayName,
        columns
      );
      if (specialDescription) {
        return specialDescription;
      }

      humanReadableFormula = this.formatReadableDescriptionFormula(humanReadableFormula);

      if (!normalizedDisplayName || humanReadableFormula.includes("=")) {
        return humanReadableFormula;
      }
      return humanReadableFormula.includes("\n")
        ? `${normalizedDisplayName} =\n${humanReadableFormula}`
        : `${normalizedDisplayName} = ${humanReadableFormula}`;
    },

    buildSpecialFormulaDescription(formula, displayName, columns = this.formulaColumns) {
      const normalizedFormula = `${formula || ""}`.replace(/\s+/g, "");
      const hasToken = (code) => {
        const foundItem = (columns || []).find((item) => item.code === code);
        const tokenCodes = [code, foundItem && foundItem.codeLabel].filter(Boolean);
        return tokenCodes.some((tokenCode) => normalizedFormula.includes(`[${tokenCode}]`));
      };
      const isOtFormula =
        hasToken("ot_150") &&
        hasToken("ot_200") &&
        hasToken("ot_300") &&
        hasToken("salary_level") &&
        hasToken("standard_work");

      if (!isOtFormula || !this.isStandardOtFormula(normalizedFormula)) {
        return "";
      }

      const otName = displayName || "OT";
      const ot150Label = this.getFormulaLabelByCode("ot_150", columns);
      const ot200Label = this.getFormulaLabelByCode("ot_200", columns);
      const ot300Label = this.getFormulaLabelByCode("ot_300", columns);
      const salaryLabel = this.getFormulaLabelByCode("salary_level", columns);
      const standardWorkLabel = this.getFormulaLabelByCode("standard_work", columns);

      return [
        `${otName} = tiền OT 150% + tiền OT 200% + tiền OT 300%.`,
        `- Tiền OT 150% = ${ot150Label} * Đơn giá giờ * 1.5`,
        `- Tiền OT 200% = ${ot200Label} * Đơn giá giờ * 2`,
        `- Tiền OT 300% = ${ot300Label} * Đơn giá giờ * 3`,
        `Trong đó: Đơn giá giờ = ${salaryLabel} / ${standardWorkLabel} / 8.`,
      ].join("\n");
    },

    isStandardOtFormula(normalizedFormula) {
      const plusCount = (normalizedFormula.match(/\+/g) || []).length;
      const has150Rate = normalizedFormula.includes("*150%") || normalizedFormula.includes("*1.5");
      const has200Rate = normalizedFormula.includes("*200%") || normalizedFormula.includes("*2");
      const has300Rate = normalizedFormula.includes("*300%") || normalizedFormula.includes("*3");

      return plusCount === 2 && has150Rate && has200Rate && has300Rate;
    },

    formatReadableDescriptionFormula(formulaText) {
      return `${formulaText || ""}`.replace(/\s+/g, " ").trim();
    },

    simplifyFormulaText(formulaText) {
      let normalizedText = `${formulaText || ""}`.trim().replace(/\s+/g, " ");

      while (this.hasWrappingParentheses(normalizedText)) {
        normalizedText = normalizedText.slice(1, -1).trim();
      }

      return normalizedText;
    },

    hasWrappingParentheses(value) {
      if (!value || value[0] !== "(" || value[value.length - 1] !== ")") {
        return false;
      }

      let depth = 0;
      for (let index = 0; index < value.length; index += 1) {
        const character = value[index];
        if (character === "(") {
          depth += 1;
        } else if (character === ")") {
          depth -= 1;
          if (depth === 0 && index < value.length - 1) {
            return false;
          }
        }
      }

      return depth === 0;
    },

    insertFieldToken(row) {
      const codeToken = `[${this.getVisibleCode(row)}]`;
      this.insertTextAtCursor(codeToken);
    },

    insertOperator(operator) {
      const operatorText = operator === "," ? ", " : ` ${operator} `;
      this.insertTextAtCursor(operatorText);
    },

    insertTextAtCursor(text) {
      const textarea =
        this.$refs.formulaEditor &&
        this.$refs.formulaEditor.$el &&
        this.$refs.formulaEditor.$el.querySelector("textarea");

      if (!textarea) {
        this.formulaEditorValue += text;
        this.syncCurrentFormulaDraft();
        return;
      }

      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;
      this.formulaEditorValue =
        this.formulaEditorValue.slice(0, start) +
        text +
        this.formulaEditorValue.slice(end);

      this.syncCurrentFormulaDraft();

      this.$nextTick(() => {
        textarea.focus();
        const nextPosition = start + text.length;
        textarea.setSelectionRange(nextPosition, nextPosition);
      });
    },

    resetFormulaInput() {
      this.formulaEditorValue = "";
    },

    validateFormulaExpression(formulaEditorStr, validColumnCodes) {
      const formula = `${formulaEditorStr || ""}`.trim();

      if (!formula) {
        return { valid: false, message: "Công thức tính không được để trống." };
      }

      const tokens = [];
      let i = 0;
      while (i < formula.length) {
        const ch = formula[i];

        if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
          i++;
          continue;
        }

        if (ch === "[") {
          const closeIdx = formula.indexOf("]", i + 1);
          if (closeIdx === -1) {
            return { valid: false, message: "Công thức không hợp lệ: thiếu dấu ']' đóng mã cột." };
          }
          const innerCode = formula.slice(i + 1, closeIdx).trim();
          if (!innerCode) {
            return { valid: false, message: "Công thức không hợp lệ: mã cột không được rỗng []." };
          }
          if (!validColumnCodes.includes(innerCode)) {
            return { valid: false, message: `Mã cột [${innerCode}] không tồn tại trong danh sách chỉ tiêu.` };
          }
          tokens.push({ type: "COLUMN", value: innerCode });
          i = closeIdx + 1;
          continue;
        }

        if (ch === "]") {
          return { valid: false, message: "Công thức không hợp lệ: dấu ']' không có dấu '[' tương ứng." };
        }

        if ((ch >= "0" && ch <= "9") || (ch === "." && i + 1 < formula.length && formula[i + 1] >= "0" && formula[i + 1] <= "9")) {
          let numStr = "";
          while (i < formula.length && ((formula[i] >= "0" && formula[i] <= "9") || formula[i] === ".")) {
            numStr += formula[i];
            i++;
          }
          if (i < formula.length && formula[i] === "%") {
            const afterPct = i + 1 < formula.length ? formula[i + 1] : "\0";
            if ((afterPct >= "0" && afterPct <= "9") || afterPct === "." ||
                (afterPct >= "a" && afterPct <= "z") || (afterPct >= "A" && afterPct <= "Z") ||
                afterPct === "_" || afterPct === "[") {
              return { valid: false, message: `Công thức không hợp lệ: '${numStr}%' phải đứng độc lập, không thể nối thêm ký tự sau dấu '%'.` };
            }
            i++;
            tokens.push({ type: "PERCENT_NUMBER", value: numStr });
          } else {
            tokens.push({ type: "NUMBER", value: numStr });
          }
          continue;
        }

        if (ch === "+" || ch === "-" || ch === "*" || ch === "/") {
          tokens.push({ type: "OPERATOR", value: ch });
          i++;
          continue;
        }

        if (ch === "(") {
          tokens.push({ type: "LEFT_PAREN", value: ch });
          i++;
          continue;
        }
        if (ch === ")") {
          tokens.push({ type: "RIGHT_PAREN", value: ch });
          i++;
          continue;
        }

        if (ch === ",") {
          tokens.push({ type: "COMMA", value: ch });
          i++;
          continue;
        }

        if ((ch >= "a" && ch <= "z") || (ch >= "A" && ch <= "Z") || ch === "_") {
          let identifier = "";
          while (i < formula.length && (/[a-zA-Z0-9_]/.test(formula[i]))) {
            identifier += formula[i];
            i++;
          }
          const nextNonSpace = formula.slice(i).trimStart();
          if (nextNonSpace.startsWith("(")) {
            tokens.push({ type: "FUNC", value: identifier });
          } else if (validColumnCodes.includes(identifier)) {
            tokens.push({ type: "COLUMN", value: identifier });
          } else {
            return { valid: false, message: `Ký tự không hợp lệ trong công thức: '${identifier}'.` };
          }
          continue;
        }

        return { valid: false, message: `Ký tự '${ch}' không hợp lệ trong công thức.` };
      }

      if (tokens.length === 0) {
        return { valid: false, message: "Công thức tính không được để trống." };
      }

      let expectOperand = true;
      let parenBalance = 0;

      for (let t = 0; t < tokens.length; t++) {
        const token = tokens[t];
        const prev = t > 0 ? tokens[t - 1] : null;
        const next = t < tokens.length - 1 ? tokens[t + 1] : null;

        if (token.type === "LEFT_PAREN") {
          if (!expectOperand && (prev && (prev.type === "COLUMN" || prev.type === "NUMBER" || prev.type === "RIGHT_PAREN"))) {
            return { valid: false, message: "Công thức không hợp lệ: thiếu toán tử trước dấu '('." };
          }
          parenBalance++;
          if (next && next.type === "RIGHT_PAREN") {
            return { valid: false, message: "Công thức không hợp lệ: ngoặc rỗng ()." };
          }
          expectOperand = true;
          continue;
        }

        if (token.type === "RIGHT_PAREN") {
          parenBalance--;
          if (parenBalance < 0) {
            return { valid: false, message: "Công thức không hợp lệ: thừa dấu ')'." };
          }
          if (expectOperand) {
            return { valid: false, message: "Công thức không hợp lệ: thiếu toán hạng trước dấu ')'." };
          }
          expectOperand = false;
          continue;
        }

        if (token.type === "OPERATOR") {
          const isUnaryMinus =
            token.value === "-" &&
            (t === 0 ||
              (prev && (prev.type === "OPERATOR" || prev.type === "LEFT_PAREN" || prev.type === "COMMA")));

          if (isUnaryMinus) {
            continue;
          }

          if (expectOperand) {
            return {
              valid: false,
              message: `Công thức không hợp lệ: toán tử '${token.value}' không hợp lệ ở vị trí này.`,
            };
          }
          expectOperand = true;
          continue;
        }

        if (token.type === "COMMA") {
          if (expectOperand) {
            return { valid: false, message: "Công thức không hợp lệ: dấu ',' không hợp lệ ở vị trí này." };
          }
          expectOperand = true;
          continue;
        }

        if (token.type === "COLUMN" || token.type === "NUMBER" || token.type === "PERCENT_NUMBER") {
          if (!expectOperand) {
            return { valid: false, message: "Công thức không hợp lệ: thiếu toán tử giữa hai toán hạng." };
          }
          expectOperand = false;
          continue;
        }

        if (token.type === "FUNC") {
          if (!expectOperand) {
            return { valid: false, message: "Công thức không hợp lệ: thiếu toán tử trước hàm." };
          }
          continue;
        }
      }

      if (expectOperand) {
        const lastToken = tokens[tokens.length - 1];
        if (lastToken.type === "OPERATOR") {
          return {
            valid: false,
            message: `Công thức không hợp lệ: thiếu toán hạng sau toán tử '${lastToken.value}'.`,
          };
        }
        return { valid: false, message: "Công thức không hợp lệ: thiếu toán hạng." };
      }

      if (parenBalance > 0) {
        return { valid: false, message: "Công thức không hợp lệ: thiếu dấu ')' đóng ngoặc." };
      }

      return { valid: true, message: "" };
    },

    async saveFormula() {
      const targetIndex = this.formulaColumns.findIndex((item) => item.code === this.currentFormulaCode);
      if (targetIndex === -1) {
        return;
      }

      this.syncCurrentFormulaDraft();

      const validColumnCodes = (this.formulaColumns || []).reduce((acc, col) => {
        if (col.code) acc.push(col.code);
        if (col.codeLabel) acc.push(col.codeLabel);
        return acc;
      }, []);

      const editorFormula = this.formulaEditorValue;
      const validationResult = this.validateFormulaExpression(editorFormula, validColumnCodes);
      if (!validationResult.valid) {
        if (window.toastr) {
          window.toastr.error(validationResult.message || "Công thức không hợp lệ.");
        }
        return;
      }

      const rawFormula = this.convertEditorFormulaToRaw(editorFormula);
      const currentItem = this.formulaColumns[targetIndex] || {};
      const contractTypeFormulas =
        currentItem.contractTypeFormulas && typeof currentItem.contractTypeFormulas === "object"
          ? { ...currentItem.contractTypeFormulas }
          : { [ALL_CONTRACT_TYPE_FORMULA_KEY]: currentItem.formula || "" };

      contractTypeFormulas[this.selectedContractTypeId] = rawFormula;

      this.$set(this.formulaColumns, targetIndex, {
        ...currentItem,
        formula: contractTypeFormulas[ALL_CONTRACT_TYPE_FORMULA_KEY] || "",
        contractTypeFormulas,
        description: this.buildFormulaDescription(rawFormula, this.currentDisplayName),
      });
      this.$set(
        this.formulaDraftMap,
        this.getFormulaDraftKey(this.currentFormulaCode, this.selectedContractTypeId),
        this.formulaEditorValue
      );
      try {
        const payload = buildSalaryFormulaApiPayload(this.formulaColumns[targetIndex], targetIndex);
        const response = payload.id
          ? await SalaryFormulaConfigApi.update(payload)
          : await SalaryFormulaConfigApi.create(payload);
        const savedItem = response && response.result ? response.result : null;

        if (savedItem && savedItem.id) {
          this.$set(this.formulaColumns, targetIndex, {
            ...this.formulaColumns[targetIndex],
            id: savedItem.id,
          });
        }
        if (window.toastr) {
          window.toastr.success("Lưu công thức thành công.");
        }
      } catch (error) {
        if (window.toastr) {
          window.toastr.error("Không lưu được công thức lên máy chủ.");
        }
      }
    },

    goBack() {
      this.$router
        .push({
          name: "Admin-Salary-SalaryConfig-SalaryConfig",
          query: {
            contractTypeId: this.selectedContractTypeId,
          },
        })
        .catch(() => {});
    },

    getHeaderCellStyle() {
      return {
        background: "#f1f4f8",
        color: "#4a4a4a",
        fontWeight: "700",
      };
    },
  },
};
</script>

<style scoped>
.salary-formula-setup-page {
  --salary-formula-table-height: 554px;
  --salary-formula-panel-height: 230px;
  padding-right: 20px;
  box-sizing: border-box;
}

.salary-formula-setup__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.salary-formula-setup__title {
  margin: 0 0 8px;
  font-size: 30px;
  font-weight: 700;
  color: #2e3137;
}

.salary-formula-setup__selector {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
}

.salary-formula-setup__selector-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
}

.salary-formula-setup__selector-label {
  flex: 0 0 auto;
  color: #2e3137;
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
}

.salary-formula-setup__selector-input {
  flex: 0 0 280px;
  width: 280px;
  max-width: 280px;
  min-width: 0;
}

.salary-formula-setup__selector-input--contract {
  flex: 0 0 280px;
}

.salary-formula-setup__actions {
  display: flex;
  gap: 12px;
}

.salary-formula-setup__button {
  min-width: 78px;
  border-color: #ff9958;
  border-radius: 12px;
  background: #ff9958;
  color: #ffffff;
}

.salary-formula-setup__button:hover,
.salary-formula-setup__button:focus {
  border-color: #ff8b42;
  background: #ff8b42;
  color: #ffffff;
}

.salary-formula-setup__hint {
  margin-bottom: 16px;
  color: #5d6a79;
  line-height: 1.6;
}

.salary-formula-setup__layout {
  display: grid;
  grid-template-columns: minmax(380px, 1fr) minmax(420px, 1fr);
  gap: 22px;
  align-items: stretch;
}

.salary-formula-setup__left,
.salary-formula-setup__right {
  display: flex;
  flex-direction: column;
}

.salary-formula-setup__section-title {
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 700;
  color: #4b5968;
}

.salary-formula-setup__table-wrap {
  flex: 0 0 auto;
  height: var(--salary-formula-table-height);
  max-height: var(--salary-formula-table-height);
  min-height: 0;
  overflow: hidden;
}

.salary-formula-setup__editor-wrap {
  flex: 0 0 auto;
  height: var(--salary-formula-panel-height);
}

.salary-formula-setup__editor ::v-deep textarea {
  height: var(--salary-formula-panel-height);
  min-height: var(--salary-formula-panel-height);
  max-height: var(--salary-formula-panel-height);
  font-family: Consolas, "Courier New", monospace;
}

.salary-formula-setup__operator-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin: 14px 0 18px;
}

.salary-formula-setup__operator-list,
.salary-formula-setup__formula-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.salary-formula-setup__formula-actions {
  justify-content: flex-end;
  margin-left: auto;
}

.salary-formula-setup__description-wrap {
  flex: 0 0 auto;
  display: flex;
  height: var(--salary-formula-panel-height);
  min-height: var(--salary-formula-panel-height);
  max-height: var(--salary-formula-panel-height);
}

.salary-formula-setup__description {
  display: block;
  flex: 1 1 auto;
  height: 100%;
  min-height: 100%;
  max-height: 100%;
  padding: 14px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #f7f9fc;
  color: #606266;
  line-height: 1.7;
  text-align: left;
  text-indent: 0;
  word-break: break-word;
  white-space: pre-line;
  overflow-y: auto;
}

.salary-formula-setup__insert-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 50%;
  background: #ffa15c;
  color: #fff;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.salary-formula-setup__insert-button:hover {
  transform: translateX(1px);
  box-shadow: 0 4px 10px rgba(255, 161, 92, 0.28);
}

.salary-formula-setup__insert-button:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 161, 92, 0.22);
}

.salary-formula-setup__insert-button i {
  font-size: 14px;
  font-weight: 700;
}

@media (max-width: 1100px) {
  .salary-formula-setup__layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .salary-formula-setup__header {
    flex-direction: column;
  }

  .salary-formula-setup__actions {
    width: 100%;
  }

  .salary-formula-setup__selector-row {
    flex-direction: column;
  }
}
</style>
